/**
 * The display scaling preference, and how it survives a page reload.
 *
 * Kept out of `main.ts` so the fallback can be tested without a DOM: the
 * shell owns *when* the preference is read and written, this module owns the
 * key.
 */
import type { ScalingMode } from '../engine/constants'

/** `localStorage` key holding the display scaling preference. */
export const SCALING_KEY = 'tavi:scaling'

/** True when the value is one of the modes the display code understands. */
function isScalingMode(value: string | null): value is ScalingMode {
  return value === 'fill' || value === 'integer'
}

/**
 * The saved mode, or `fill`. A denied or absent store falls back to the
 * default.
 */
export function loadScalingMode(
  store: Pick<Storage, 'getItem'> | null = safeStorage(),
): ScalingMode {
  if (store === null) return 'fill'
  try {
    const raw = store.getItem(SCALING_KEY)
    if (isScalingMode(raw)) return raw
  } catch {
    // The default is the only safe answer when even a read is denied.
  }
  return 'fill'
}

/** Persist the mode under the modern key. Failure is not fatal. */
export function saveScalingMode(
  mode: ScalingMode,
  store: Pick<Storage, 'setItem'> | null = safeStorage(),
): void {
  if (store === null) return
  try {
    store.setItem(SCALING_KEY, mode)
  } catch {
    /* the preference simply will not persist */
  }
}

/** `localStorage`, or null when the browser denies access to it. */
function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}
